// LayoutEditor.styles.ts
import styled, { keyframes } from "styled-components";

// ===== ANIMAÇÕES =====
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const rotate3D = keyframes`
  from { transform: rotateY(0deg); }
  to { transform: rotateY(360deg); }
`;

// ===== CONTAINER PRINCIPAL =====
export const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 12px;
  overflow: hidden;
  animation: ${scaleIn} 0.3s ease;
`;

// ===== HEADER =====
export const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--sp-20) var(--sp-24);
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colors.selection + "20"} 0%,
    ${(props) => props.theme.colors.selection + "10"} 100%
  );
  border-bottom: 2px solid ${(props) => props.theme.colors.border};
  animation: ${slideIn} 0.4s ease;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-16);

  > span {
    font-size: var(--fs-4xl);
    color: ${(props) => props.theme.colors.selection};
  }
`;

export const EditorTitle = styled.h2`
  font-size: var(--fs-2xl);
  font-weight: 700;
  margin: 0;
  color: ${(props) => props.theme.colors.text};
`;

export const EditorSubtitle = styled.p`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  margin: var(--sp-4) 0 0 0;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.label};
  cursor: pointer;
  padding: var(--sp-8);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.border};
    color: ${(props) => props.theme.colors.text};
    transform: rotate(90deg);
  }
`;

// ===== CONTENT =====
export const EditorContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  flex: 1;
  overflow: hidden;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

// ===== PREVIEW COLUMN =====
export const PreviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--sp-24);
  border-right: 1px solid ${(props) => props.theme.colors.border};
  background-color: ${(props) => props.theme.colors.background + "80"};

  @media (max-width: 1200px) {
    border-right: none;
    border-bottom: 1px solid ${(props) => props.theme.colors.border};
  }
`;

export const PreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--sp-20);
`;

export const SectionTitle = styled.h3`
  font-size: var(--fs-lg);
  font-weight: 600;
  margin: 0;
  color: ${(props) => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: var(--sp-8);
`;

export const ViewToggle = styled.div`
  display: flex;
  gap: var(--sp-4);
  background-color: ${(props) => props.theme.colors.background};
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  padding: var(--sp-4);
`;

export const ViewButton = styled.button<{ $isActive: boolean }>`
  padding: var(--sp-8) var(--sp-16);
  border: none;
  background-color: ${(props) =>
    props.$isActive ? props.theme.colors.selection : "transparent"};
  color: ${(props) =>
    props.$isActive ? props.theme.colors.text : props.theme.colors.label};
  border-radius: 6px;
  cursor: pointer;
  font-size: var(--fs-sm);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: var(--sp-6);
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.$isActive
        ? props.theme.colors.selection
        : props.theme.colors.border};
  }
`;

// ===== PREVIEW 2D =====
export const Preview2D = styled.div<{
  $wallColor: string;
  $floorColor: string;
}>`
  flex: 1;
  background-color: ${(props) => props.$wallColor};
  border-radius: 12px;
  border: 2px solid ${(props) => props.theme.colors.border};
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background-image: linear-gradient(
      45deg,
      ${(props) => props.$floorColor} 25%,
      transparent 25%
    ),
    linear-gradient(
      -45deg,
      ${(props) => props.$floorColor} 25%,
      transparent 25%
    ),
    linear-gradient(45deg, transparent 75%, ${(props) => props.$floorColor} 75%),
    linear-gradient(
      -45deg,
      transparent 75%,
      ${(props) => props.$floorColor} 75%
    );
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  animation: ${fadeIn} 0.5s ease;
`;

export const TablesContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--sp-20);
  padding: var(--sp-20);
  max-width: 90%;
  max-height: 90%;
`;

export const TableItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    z-index: 10;
  }
`;

export const TableTop = styled.div`
  width: calc(var(--u) * 50);
  height: calc(var(--u) * 50);
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border-radius: 8px;
  transform: rotate(45deg);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
  border: 2px solid #ffffff;
`;

export const TableNumber = styled.div`
  position: absolute;
  color: white;
  font-weight: 700;
  font-size: var(--fs-md);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  z-index: 1;
`;

export const FeaturesOverlay = styled.div`
  position: absolute;
  top: calc(var(--u) * 12);
  right: calc(var(--u) * 12);
  display: flex;
  flex-direction: column;
  gap: var(--sp-8);
`;

export const FeatureIcon = styled.div`
  width: calc(var(--u) * 36);
  height: calc(var(--u) * 36);
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-lg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: help;
  animation: ${pulse} 2s ease-in-out infinite;

  &:hover {
    transform: scale(1.1);
  }
`;

// ===== PREVIEW 3D =====
export const Preview3D = styled.div`
  flex: 1;
  background: linear-gradient(to bottom, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  border: 2px solid ${(props) => props.theme.colors.border};
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  perspective: 1000px;
  animation: ${fadeIn} 0.5s ease;
`;

export const Room3D = styled.div<{ $wallColor: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotateX(10deg) rotateY(-15deg);
  transform-style: preserve-3d;
  width: 80%;
  height: 70%;
  transition: transform 0.5s ease;

  &:hover {
    transform: translate(-50%, -50%) rotateX(10deg) rotateY(5deg);
  }
`;

export const Wall3D = styled.div<{ $position: string }>`
  position: absolute;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(200, 200, 200, 0.6) 100%
  );
  border: 1px solid rgba(0, 0, 0, 0.1);
  transform-style: preserve-3d;
`;

export const Floor3D = styled.div<{ $floorColor: string }>`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.$floorColor};
  transform: rotateX(90deg);
  transform-origin: bottom;
  box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.2);
`;

export const Tables3DContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10%;
  padding: 10%;
  transform-style: preserve-3d;
`;

export const Table3D = styled.div<{ $position: number }>`
  transform-style: preserve-3d;
  animation: ${fadeIn} 0.5s ease ${(props) => props.$position * 0.1}s;
`;

export const TableTop3D = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: linear-gradient(135deg, #8b4513 0%, #a0522d 100%);
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  transform: translateZ(20px);
`;

export const View3DInfo = styled.div`
  position: absolute;
  bottom: calc(var(--u) * 12);
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: var(--sp-8) var(--sp-16);
  border-radius: 20px;
  font-size: var(--fs-xs);
  font-weight: 600;
  backdrop-filter: blur(4px);
`;

// ===== MÉTRICAS RÁPIDAS =====
export const MetricsQuick = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--sp-12);
  margin-top: var(--sp-20);

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const MetricQuickItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  padding: var(--sp-16);
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: ${(props) => props.theme.colors.selection};
  }

  > span {
    color: ${(props) => props.theme.colors.selection};
    font-size: var(--fs-2xl);
  }
`;

export const MetricValue = styled.div`
  font-size: var(--fs-2xl);
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  line-height: 1;
`;

export const MetricLabel = styled.div`
  font-size: var(--fs-xs);
  color: ${(props) => props.theme.colors.label};
  margin-top: var(--sp-4);
`;

// ===== CONFIG COLUMN =====
export const ConfigColumn = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.background};
`;

export const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: var(--sp-24);

  &::-webkit-scrollbar {
    width: calc(var(--u) * 10);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.background};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.background};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.theme.colors.selectionDark};
  }
`;

export const ConfigSection = styled.div`
  margin-bottom: var(--sp-32);
  padding-bottom: var(--sp-32);
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  animation: ${slideIn} 0.4s ease;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  ${SectionTitle} {
    margin-bottom: var(--sp-20);
  }
`;

// ===== FORMULÁRIO =====
export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-8);
  margin-bottom: var(--sp-20);

  &:last-child {
    margin-bottom: 0;
  }
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-16);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const Label = styled.label<{ $required?: boolean }>`
  font-weight: 600;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
  display: flex;
  align-items: center;
  gap: var(--sp-6);

  ${(props) =>
    props.$required &&
    `
    &::after {
      content: "*";
      color: #f44336;
      margin-left: var(--sp-2);
      font-size: var(--fs-base);
    }
  `}
`;

export const Input = styled.input`
  width: 100%;
  padding: var(--sp-12) calc(var(--u) * 14);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  font-size: var(--fs-md);
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.selection + "20"};
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.label};
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: var(--sp-12) calc(var(--u) * 14);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  font-size: var(--fs-md);
  background-color: ${(props) => props.theme.colors.background};
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.selection + "20"};
  }
`;

export const NumberInput = styled(Input)`
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    opacity: 1;
    height: calc(var(--u) * 30);
  }
`;

export const ColorPickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  padding: var(--sp-8);
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
  transition: all 0.2s ease;

  &:focus-within {
    border-color: ${(props) => props.theme.colors.selection};
    box-shadow: 0 0 0 3px ${(props) => props.theme.colors.selection + "20"};
  }
`;

export const ColorInput = styled.input`
  width: calc(var(--u) * 50);
  height: calc(var(--u) * 40);
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 6px;
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

export const ColorValue = styled.span`
  font-size: var(--fs-md);
  font-family: monospace;
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const Hint = styled.span`
  font-size: var(--fs-xs);
  color: ${(props) => props.theme.colors.label};
  font-style: italic;
`;

// ===== FEATURES =====
export const AddFeatureRow = styled.div`
  display: flex;
  gap: var(--sp-8);
`;

export const AddButton = styled.button`
  padding: var(--sp-12) var(--sp-16);
  background-color: ${(props) => props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  min-width: calc(var(--u) * 48);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.selection + "40"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const FeaturesGrid = styled.div`
  display: flex;
  gap: var(--sp-8);
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;
`;

export const FeatureTag = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-8) var(--sp-12);
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colors.selection + "20"} 0%,
    ${(props) => props.theme.colors.selection + "10"} 100%
  );
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  border-radius: 20px;
  font-size: var(--fs-sm);
  font-weight: 500;
  color: ${(props) => props.theme.colors.selection};
  animation: ${scaleIn} 0.2s ease;

  span {
    user-select: none;
  }
`;

export const RemoveFeatureButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.selection};
  cursor: pointer;
  padding: 0;
  width: calc(var(--u) * 18);
  height: calc(var(--u) * 18);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.selection};
    color: ${(props) => props.theme.colors.textOnPrimary};
    transform: rotate(90deg);
  }

  span {
    font-size: var(--fs-base);
  }
`;

// ===== ATMOSFERA =====
export const AtmosphereGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-12);
  margin-top: var(--sp-12);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const AtmosphereItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-16);
  background-color: ${(props) => props.theme.colors.background + "80"};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: ${(props) => props.theme.colors.selection};
  }

  span:first-child {
    font-size: var(--fs-2xl);
    color: ${(props) => props.theme.colors.selection};
  }

  span:last-child {
    font-size: var(--fs-xs);
    font-weight: 600;
    color: ${(props) => props.theme.colors.text};
  }
`;

// ===== FOOTER =====
export const EditorFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--sp-20) var(--sp-24);
  background-color: ${(props) => props.theme.colors.background + "80"};
  border-top: 2px solid ${(props) => props.theme.colors.border};
  animation: ${slideIn} 0.4s ease;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--sp-16);
  }
`;

export const FooterInfo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-sm);
  color: ${(props) => props.theme.colors.label};
  font-style: italic;

  span {
    color: ${(props) => props.theme.colors.selection};
  }
`;

export const FooterActions = styled.div`
  display: flex;
  gap: var(--sp-12);

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column-reverse;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

export const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-8);
  padding: var(--sp-12) calc(var(--u) * 28);
  background-color: ${(props) => props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: calc(var(--u) * 160);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px ${(props) => props.theme.colors.selection + "40"};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-8);
  padding: var(--sp-12) calc(var(--u) * 28);
  background-color: transparent;
  color: ${(props) => props.theme.colors.text};
  border: 2px solid ${(props) => props.theme.colors.border};
  border-radius: 8px;
  font-weight: 500;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: calc(var(--u) * 120);

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.background};
    border-color: ${(props) => props.theme.colors.text + "40"};
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonSpinner = styled.div`
  width: calc(var(--u) * 16);
  height: calc(var(--u) * 16);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

// ===== MATERIAL ICONS =====
export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

// ===== EXPORTAÇÃO =====
export default {
  EditorContainer,
  EditorHeader,
  HeaderLeft,
  EditorTitle,
  EditorSubtitle,
  CloseButton,
  EditorContent,
  PreviewColumn,
  PreviewHeader,
  SectionTitle,
  ViewToggle,
  ViewButton,
  Preview2D,
  TablesContainer,
  TableItem,
  TableTop,
  TableNumber,
  FeaturesOverlay,
  FeatureIcon,
  Preview3D,
  Room3D,
  Wall3D,
  Floor3D,
  Tables3DContainer,
  Table3D,
  TableTop3D,
  View3DInfo,
  MetricsQuick,
  MetricQuickItem,
  MetricValue,
  MetricLabel,
  ConfigColumn,
  ScrollableContent,
  ConfigSection,
  FormGroup,
  FormRow,
  Label,
  Input,
  Select,
  NumberInput,
  ColorPickerWrapper,
  ColorInput,
  ColorValue,
  Hint,
  AddFeatureRow,
  AddButton,
  FeaturesGrid,
  FeatureTag,
  RemoveFeatureButton,
  AtmosphereGrid,
  AtmosphereItem,
  EditorFooter,
  FooterInfo,
  FooterActions,
  PrimaryButton,
  SecondaryButton,
  ButtonSpinner,
  MaterialIcons,
};
